/* eslint-disable unicorn/prefer-module */
import { useCallback, useMemo, useState } from "react";
import { ImCheckboxChecked, ImCheckboxUnchecked } from "react-icons/im";
import cx from "classix";
import { BadgeDato, CategoriaLeyendaIpsos, Loader } from "..";
import {
  ESCENARIOS_CAPTACION_RETAIL,
  INVERSION_REFERENCIA_3M_RETAIL,
  IPSOS_CONVENIENCIA,
  IPSOS_SUPER,
} from "../../data/ipsos2025/retail";
import { useCategoriaMoneda, useUniversoCategorias } from "../../utils";
import { Header } from "../Header";
import styles from "./CategoriaRetail.module.scss";

export const Retail = () => {
  const { formatNumber, formatLocal, convertCLP, pais } = useCategoriaMoneda();
  const {
    edificios,
    loading,
    error,
    checkAll,
    checkedBuildings,
    toggleCheck,
    toggleCheckAll,
    cantidadEdificios,
    totalPantallas,
    totalEjecutivos,
    totalEmpresas,
  } = useUniversoCategorias(pais);
  const [showInventarioModal, setShowInventarioModal] = useState(false);
  const [supermercadoSeleccionado, setSupermercadoSeleccionado] = useState("Líder");
  const [convenienciaSeleccionada, setConvenienciaSeleccionada] = useState("Oxxo");
  const [porcentajeCaptacion, setPorcentajeCaptacion] = useState(4);
  const [vistaActiva, setVistaActiva] = useState("super"); // 'super' o 'conveniencia'
  const [verAnual, setVerAnual] = useState(false);
  const [showOportunidadModal, setShowOportunidadModal] = useState(false);

  // =====================================================
  // CÁLCULOS SUPERMERCADOS
  // =====================================================
  const resultadosSuper = useMemo(() => {
    if (!totalEjecutivos) return null;

    const compranSuper = Math.round(totalEjecutivos * IPSOS_SUPER.penetracion.valor);
    const noCompran = Math.round(totalEjecutivos * IPSOS_SUPER.noPenetracion.valor);

    // Desglose por frecuencia
    const desgloseFrecuencia = {};
    let consumosMensuales = 0;
    let mercadoMensual = 0;

    Object.entries(IPSOS_SUPER.frecuenciaCompra).forEach(([freq, data]) => {
      const personas = Math.round(compranSuper * data.pct.valor);
      const consumos = Math.round(personas * data.factor.valor);
      const gasto = consumos * data.ticket.valor;

      desgloseFrecuencia[freq] = {
        pctDato: data.pct,
        pct: data.pct.valor,
        personas,
        consumos,
        ticket: data.ticket.valor,
        gasto,
      };

      consumosMensuales += consumos;
      mercadoMensual += gasto;
    });

    // Market share por supermercado
    const marketShareData = {};
    Object.entries(IPSOS_SUPER.marketShare).forEach(([nombre, dato]) => {
      marketShareData[nombre] = {
        dato,
        share: dato.valor,
        clientes: Math.round(compranSuper * dato.valor),
        mercadoMensual: Math.round(mercadoMensual * dato.valor),
      };
    });

    return {
      compranSuper,
      noCompran,
      desgloseFrecuencia,
      consumosMensuales,
      consumosAnuales: consumosMensuales * 12,
      mercadoMensual,
      mercadoAnual: mercadoMensual * 12,
      marketShareData,
    };
  }, [totalEjecutivos]);

  // =====================================================
  // CÁLCULOS TIENDAS DE CONVENIENCIA
  // =====================================================
  const resultadosConveniencia = useMemo(() => {
    if (!totalEjecutivos) return null;

    const usanConveniencia = Math.round(totalEjecutivos * IPSOS_CONVENIENCIA.penetracion.valor);
    const noUsan = Math.round(totalEjecutivos * IPSOS_CONVENIENCIA.noPenetracion.valor);

    // Desglose por frecuencia
    const desgloseFrecuencia = {};
    let consumosMensuales = 0;

    Object.entries(IPSOS_CONVENIENCIA.frecuenciaUso).forEach(([freq, data]) => {
      const personas = Math.round(usanConveniencia * data.pct.valor);
      const consumos = Math.round(personas * data.factor.valor);

      desgloseFrecuencia[freq] = {
        pctDato: data.pct,
        pct: data.pct.valor,
        personas,
        consumos,
      };

      consumosMensuales += consumos;
    });

    const mercadoMensual = consumosMensuales * IPSOS_CONVENIENCIA.ticketPromedio.valor;

    // Market share por tienda
    const marketShareData = {};
    Object.entries(IPSOS_CONVENIENCIA.marketShare).forEach(([nombre, dato]) => {
      marketShareData[nombre] = {
        dato,
        share: dato.valor,
        clientes: Math.round(usanConveniencia * dato.valor),
        mercadoMensual: Math.round(mercadoMensual * dato.valor),
      };
    });

    return {
      usanConveniencia,
      noUsan,
      desgloseFrecuencia,
      consumosMensuales,
      consumosAnuales: consumosMensuales * 12,
      mercadoMensual,
      mercadoAnual: mercadoMensual * 12,
      marketShareData,
    };
  }, [totalEjecutivos]);

  // =====================================================
  // CÁLCULO OPORTUNIDAD CAPTACIÓN SUPERMERCADOS
  // =====================================================
  const calcularOportunidadSuper = useCallback(
    (supermercado, pctCaptacion) => {
      if (!resultadosSuper) return null;

      const datosSuper = resultadosSuper.marketShareData[supermercado];
      if (!datosSuper) return null;

      // Captar % del mercado total
      const mercadoCaptadoMensual = Math.round(resultadosSuper.mercadoMensual * (pctCaptacion / 100));

      return {
        mercadoTotal: resultadosSuper.mercadoMensual,
        mercadoCaptadoMensual,
        mercadoCaptadoAnual: mercadoCaptadoMensual * 12,
        pctDelTotal: pctCaptacion,
      };
    },
    [resultadosSuper]
  );

  // =====================================================
  // CÁLCULO OPORTUNIDAD CAPTACIÓN CONVENIENCIA
  // =====================================================
  const calcularOportunidadConveniencia = useCallback(
    (tienda, pctCaptacion) => {
      if (!resultadosConveniencia) return null;

      const datosTienda = resultadosConveniencia.marketShareData[tienda];
      if (!datosTienda) return null;

      // Captar % del mercado total
      const mercadoCaptadoMensual = Math.round(resultadosConveniencia.mercadoMensual * (pctCaptacion / 100));

      return {
        mercadoTotal: resultadosConveniencia.mercadoMensual,
        mercadoCaptadoMensual,
        mercadoCaptadoAnual: mercadoCaptadoMensual * 12,
        pctDelTotal: pctCaptacion,
      };
    },
    [resultadosConveniencia]
  );

  if (loading) return <Loader />;
  if (error) return <p className={styles.disclaimer}>{error}</p>;

  const frecuenciaLabels = {
    diario: "Diariamente",
    variasSemana: "Varias veces por semana",
    semanal: "Una vez por semana",
    quincenal: "Cada dos semanas",
    mensual: "Una vez al mes o menos",
  };

  return (
    <div className={styles.retailPage}>
      <Header leftContent={<h1 className={styles.headerTitulo}>TIENDAS & CONVENIENCIAS</h1>}>
        <div className={styles.inventarioHeader}>
          <div className={styles.inventarioInfo}>
            <span>
              <strong>{cantidadEdificios}</strong> edificios
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalPantallas)}</strong> pantallas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEjecutivos)}</strong> personas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEmpresas)}</strong> empresas
            </span>
          </div>
          <button type="button" className={styles.inventarioBtn} onClick={() => setShowInventarioModal(true)}>
            Inventario
          </button>
        </div>
      </Header>

      <CategoriaLeyendaIpsos />

      <div className={styles.retailContent}>
        {/* Tabs para cambiar entre Supermercados y Conveniencia */}
        <div className={styles.tabsContainer}>
          <button
            type="button"
            className={cx(styles.tab, vistaActiva === "super" && styles.tabActivo)}
            onClick={() => setVistaActiva("super")}
          >
            Supermercados
          </button>
          <button
            type="button"
            className={cx(styles.tab, vistaActiva === "conveniencia" && styles.tabActivo)}
            onClick={() => setVistaActiva("conveniencia")}
          >
            Tiendas de Conveniencia
          </button>
        </div>

        {/* =====================================================
            VISTA SUPERMERCADOS
            ===================================================== */}
        {vistaActiva === "super" && resultadosSuper && (
          <>
            {/* Tabla de frecuencia */}
            <div className={styles.tablaContainer}>
              <table className={cx("table table-sm", styles.tablaResultados)}>
                <thead className="table-dark">
                  <tr>
                    <th className={cx("text-center", styles.thNoCompran)}>No compran</th>
                    <th className={cx("text-center", styles.thSiCompran)}>Sí compran</th>
                    <th className={cx("text-center", styles.thFrecuencia)}>Frecuencia de compra</th>
                    <th className={cx("text-center", styles.thPersonas)}>Personas</th>
                    <th className={cx("text-center", styles.thConsumo)}>Consumo prom.</th>
                    <th className={cx("text-center", styles.thConsumos)}>
                      <div className={styles.headerConsumos}>
                        <span>Consumos</span>
                        <div className={styles.toggleContainer}>
                          <span className={cx(styles.toggleLabel, !verAnual && styles.toggleActive)}>mes</span>
                          <label className={styles.toggleSwitch}>
                            <input type="checkbox" checked={verAnual} onChange={(e) => setVerAnual(e.target.checked)} />
                            <span className={styles.toggleSlider} />
                          </label>
                          <span className={cx(styles.toggleLabel, verAnual && styles.toggleActive)}>año</span>
                        </div>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(resultadosSuper.desgloseFrecuencia).map(([freq, data], idx) => (
                    <tr key={freq}>
                      {idx === 0 && (
                        <>
                          <td rowSpan={5} className={cx("text-center align-middle", styles.celdaNo)}>
                            <div className={styles.celdaContenido}>
                              <BadgeDato dato={IPSOS_SUPER.noPenetracion}>
                                {(IPSOS_SUPER.noPenetracion.valor * 100).toFixed(0)}%
                              </BadgeDato>
                              <div className={styles.valorGrande}>{formatNumber(resultadosSuper.noCompran)}</div>
                            </div>
                          </td>
                          <td rowSpan={5} className={cx("text-center align-middle", styles.celdaSi)}>
                            <div className={styles.celdaContenido}>
                              <BadgeDato dato={IPSOS_SUPER.penetracion}>
                                {(IPSOS_SUPER.penetracion.valor * 100).toFixed(0)}%
                              </BadgeDato>
                              <div className={styles.valorGrande}>{formatNumber(resultadosSuper.compranSuper)}</div>
                            </div>
                          </td>
                        </>
                      )}
                      <td className={styles.celdaFrecuencia}>
                        <BadgeDato dato={data.pctDato}>{(data.pct * 100).toFixed(0)}%</BadgeDato>
                        <span className={styles.frecuenciaTexto}>{frecuenciaLabels[freq]}</span>
                      </td>
                      <td className="text-center">{formatNumber(data.personas)}</td>
                      <td className="text-center">{formatLocal(convertCLP(data.ticket))}</td>
                      <td className="text-center">{formatNumber(verAnual ? data.consumos * 12 : data.consumos)}</td>
                    </tr>
                  ))}
                  <tr className={styles.filaTotales}>
                    <td colSpan={5} className={styles.celdaVacia} />
                    <td className={cx("text-center", styles.celdaTotal)}>
                      <strong>
                        {formatNumber(verAnual ? resultadosSuper.consumosAnuales : resultadosSuper.consumosMensuales)}
                      </strong>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Mercado total */}
            <div className={styles.mercadoTotalContainer}>
              <div className={styles.mercadoTotal}>
                <span className={styles.mercadoLabel}>Mercado {verAnual ? "anual" : "mensual"}:</span>
                <span className={styles.mercadoValor}>
                  {formatLocal(convertCLP(verAnual ? resultadosSuper.mercadoAnual : resultadosSuper.mercadoMensual))}
                </span>
              </div>
            </div>

            {/* Market Share */}
            <div className={styles.marketShareContainer}>
              <h4 className={styles.marketShareTitulo}>Market Share por Supermercado</h4>
              <div className={styles.marketShareGrid}>
                {Object.entries(resultadosSuper.marketShareData).map(([nombre, data]) => (
                  <div key={nombre} className={styles.marketShareCard}>
                    <div className={styles.cardNombre}>{nombre}</div>
                    <div className={styles.cardShare}>
                      <BadgeDato dato={data.dato}>{(data.share * 100).toFixed(0)}%</BadgeDato>
                    </div>
                    <div className={styles.cardMetricas}>
                      <div>
                        <span className={styles.metricaLabel}>Clientes</span>
                        <span className={styles.metricaValor}>{formatNumber(data.clientes)}</span>
                      </div>
                      <div>
                        <span className={styles.metricaLabel}>Mercado/{verAnual ? "año" : "mes"}</span>
                        <span className={styles.metricaValor}>
                          {formatLocal(convertCLP(verAnual ? data.mercadoMensual * 12 : data.mercadoMensual))}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <button type="button" className={styles.simularBtn} onClick={() => setShowOportunidadModal(true)}>
                Simular Oportunidad
              </button>
            </div>

            {/* Disclaimer */}
            <div className={styles.disclaimer}>
              <p>
                <strong>Ipsos 2025 (lámina 64, base 400/338):</strong> motivaciones de preferencia: cercanía al
                hogar/trabajo (48,4%), ofertas (39,5%), precios bajos (38%), variedad de productos (26,7%).
              </p>
              <p>
                <strong>Supuestos comerciales:</strong> visitas por mes y ticket promedio por frecuencia de compra.
              </p>
            </div>
          </>
        )}

        {/* =====================================================
            VISTA TIENDAS DE CONVENIENCIA
            ===================================================== */}
        {vistaActiva === "conveniencia" && resultadosConveniencia && (
          <>
            {/* Tabla de frecuencia */}
            <div className={styles.tablaContainer}>
              <table className={cx("table table-sm", styles.tablaResultados)}>
                <thead className="table-dark">
                  <tr>
                    <th className={cx("text-center", styles.thNoCompran)}>No usan</th>
                    <th className={cx("text-center", styles.thSiCompran)}>Sí usan</th>
                    <th className={cx("text-center", styles.thFrecuencia)}>Frecuencia de uso</th>
                    <th className={cx("text-center", styles.thPersonas)}>Personas</th>
                    <th className={cx("text-center", styles.thConsumos)}>
                      <div className={styles.headerConsumos}>
                        <span>Visitas</span>
                        <div className={styles.toggleContainer}>
                          <span className={cx(styles.toggleLabel, !verAnual && styles.toggleActive)}>mes</span>
                          <label className={styles.toggleSwitch}>
                            <input type="checkbox" checked={verAnual} onChange={(e) => setVerAnual(e.target.checked)} />
                            <span className={styles.toggleSlider} />
                          </label>
                          <span className={cx(styles.toggleLabel, verAnual && styles.toggleActive)}>año</span>
                        </div>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(resultadosConveniencia.desgloseFrecuencia).map(([freq, data], idx) => (
                    <tr key={freq}>
                      {idx === 0 && (
                        <>
                          <td rowSpan={5} className={cx("text-center align-middle", styles.celdaNo)}>
                            <div className={styles.celdaContenido}>
                              <BadgeDato dato={IPSOS_CONVENIENCIA.noPenetracion}>
                                {(IPSOS_CONVENIENCIA.noPenetracion.valor * 100).toFixed(0)}%
                              </BadgeDato>
                              <div className={styles.valorGrande}>{formatNumber(resultadosConveniencia.noUsan)}</div>
                            </div>
                          </td>
                          <td rowSpan={5} className={cx("text-center align-middle", styles.celdaSi)}>
                            <div className={styles.celdaContenido}>
                              <BadgeDato dato={IPSOS_CONVENIENCIA.penetracion}>
                                {(IPSOS_CONVENIENCIA.penetracion.valor * 100).toFixed(0)}%
                              </BadgeDato>
                              <div className={styles.valorGrande}>
                                {formatNumber(resultadosConveniencia.usanConveniencia)}
                              </div>
                            </div>
                          </td>
                        </>
                      )}
                      <td className={styles.celdaFrecuencia}>
                        <BadgeDato dato={data.pctDato}>{(data.pct * 100).toFixed(0)}%</BadgeDato>
                        <span className={styles.frecuenciaTexto}>{frecuenciaLabels[freq]}</span>
                      </td>
                      <td className="text-center">{formatNumber(data.personas)}</td>
                      <td className="text-center">{formatNumber(verAnual ? data.consumos * 12 : data.consumos)}</td>
                    </tr>
                  ))}
                  <tr className={styles.filaTotales}>
                    <td colSpan={4} className={styles.celdaVacia} />
                    <td className={cx("text-center", styles.celdaTotal)}>
                      <strong>
                        {formatNumber(
                          verAnual ? resultadosConveniencia.consumosAnuales : resultadosConveniencia.consumosMensuales
                        )}
                      </strong>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Ticket y Mercado */}
            <div className={styles.ticketMercadoContainer}>
              <div className={styles.ticketBox}>
                <span className={styles.ticketLabel}>Ticket promedio (supuesto)</span>
                <span className={styles.ticketValor}>
                  {formatLocal(convertCLP(IPSOS_CONVENIENCIA.ticketPromedio.valor))}
                </span>
              </div>
              <div className={styles.mercadoBox}>
                <span className={styles.mercadoLabel}>Mercado {verAnual ? "anual" : "mensual"}</span>
                <span className={styles.mercadoValor}>
                  {formatLocal(
                    convertCLP(verAnual ? resultadosConveniencia.mercadoAnual : resultadosConveniencia.mercadoMensual)
                  )}
                </span>
              </div>
            </div>

            {/* Market Share */}
            <div className={styles.marketShareContainer}>
              <h4 className={styles.marketShareTitulo}>Market Share por Tienda</h4>
              <div className={styles.marketShareGrid}>
                {Object.entries(resultadosConveniencia.marketShareData).map(([nombre, data]) => (
                  <div key={nombre} className={styles.marketShareCard}>
                    <div className={styles.cardNombre}>{nombre}</div>
                    <div className={styles.cardShare}>
                      <BadgeDato dato={data.dato}>{(data.share * 100).toFixed(0)}%</BadgeDato>
                    </div>
                    <div className={styles.cardMetricas}>
                      <div>
                        <span className={styles.metricaLabel}>Clientes</span>
                        <span className={styles.metricaValor}>{formatNumber(data.clientes)}</span>
                      </div>
                      <div>
                        <span className={styles.metricaLabel}>Mercado/{verAnual ? "año" : "mes"}</span>
                        <span className={styles.metricaValor}>
                          {formatLocal(convertCLP(verAnual ? data.mercadoMensual * 12 : data.mercadoMensual))}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <button type="button" className={styles.simularBtn} onClick={() => setShowOportunidadModal(true)}>
                Simular Oportunidad
              </button>
            </div>

            {/* Disclaimer */}
            <div className={styles.disclaimer}>
              <p>
                <strong>Ipsos 2025 (lámina 66, base 400):</strong> 63,2% la usa diariamente o varias veces por semana
                (18,2% + 45,0%).
              </p>
              <p>
                <strong>Supuestos comerciales:</strong> visitas por mes por frecuencia. Ticket promedio estimado:{" "}
                {formatLocal(convertCLP(IPSOS_CONVENIENCIA.ticketPromedio.valor))}.
              </p>
            </div>
          </>
        )}
      </div>

      {/* Modal de Inventario */}
      {showInventarioModal && (
        <div className={styles.modalOverlay} onClick={() => setShowInventarioModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Inventario de Edificios</h3>
              <button type="button" className={styles.modalClose} onClick={() => setShowInventarioModal(false)}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.modalSummary}>
                <span>
                  <strong>{cantidadEdificios}</strong> edificios seleccionados
                </span>
                <span>
                  <strong>{formatNumber(totalEjecutivos)}</strong> ejecutivos
                </span>
                <button type="button" className={styles.toggleAllBtn} onClick={toggleCheckAll}>
                  {checkAll ? "Deseleccionar todos" : "Seleccionar todos"}
                </button>
              </div>
              <table className="table table-sm table-hover">
                <thead className="table-dark">
                  <tr>
                    <th className="text-center">✓</th>
                    <th className="text-center">Edificio</th>
                    <th className="text-center">Ubicación</th>
                    <th className="text-center">Pantallas</th>
                    <th className="text-center">Personas</th>
                  </tr>
                </thead>
                <tbody>
                  {edificios.map(({ id, edificio, ubicacion, personas, pantallas, espera, totems, cowork }) => (
                    <tr
                      key={id}
                      onClick={() => toggleCheck(id)}
                      className={cx(checkedBuildings[id] && "table-success")}
                      style={{ cursor: "pointer" }}
                    >
                      <td className="text-center">
                        {checkedBuildings[id] ? <ImCheckboxChecked size={16} /> : <ImCheckboxUnchecked size={16} />}
                      </td>
                      <td className="text-center">{edificio.toUpperCase()}</td>
                      <td className="text-center">{ubicacion}</td>
                      <td className="text-center">
                        {formatNumber((pantallas || 0) + (espera || 0) + (totems || 0) + (cowork || 0))}
                      </td>
                      <td className="text-center">{personas ? formatNumber(personas) : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className={styles.modalFooter}>
              <button type="button" className={styles.modalBtn} onClick={() => setShowInventarioModal(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Oportunidad */}
      {showOportunidadModal && (resultadosSuper || resultadosConveniencia) && (
        <div className={styles.modalOverlay} onClick={() => setShowOportunidadModal(false)}>
          <div className={cx(styles.modalContent, styles.modalOportunidad)} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>
                Simulador de Oportunidad - {vistaActiva === "super" ? "Supermercados" : "Tiendas de Conveniencia"}
              </h3>
              <button type="button" className={styles.modalClose} onClick={() => setShowOportunidadModal(false)}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              {vistaActiva === "super" && resultadosSuper && (
                <div className={styles.simuladorContainer}>
                  <div className={styles.simuladorControles}>
                    <div className={styles.controlGroup}>
                      <label>Supermercado objetivo:</label>
                      <select
                        value={supermercadoSeleccionado}
                        onChange={(e) => setSupermercadoSeleccionado(e.target.value)}
                        className="form-select form-select-sm"
                      >
                        {Object.keys(IPSOS_SUPER.marketShare).map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className={styles.controlGroup}>
                      <label>% captación mercado (supuesto):</label>
                      <select
                        value={porcentajeCaptacion}
                        onChange={(e) => setPorcentajeCaptacion(Number(e.target.value))}
                        className="form-select form-select-sm"
                      >
                        {ESCENARIOS_CAPTACION_RETAIL.map((p) => (
                          <option key={p.valor} value={p.valor}>
                            {p.valor}%
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {(() => {
                    const oportunidad = calcularOportunidadSuper(supermercadoSeleccionado, porcentajeCaptacion);
                    if (!oportunidad) return null;

                    return (
                      <div className={styles.simuladorResultados}>
                        <div className={styles.resultadoItem}>
                          <span className={styles.resultadoLabel}>Mercado total mensual:</span>
                          <span className={styles.resultadoValor}>
                            {formatLocal(convertCLP(oportunidad.mercadoTotal))}
                          </span>
                        </div>
                        <div className={styles.resultadoItem}>
                          <span className={styles.resultadoLabel}>Captación al {porcentajeCaptacion}%:</span>
                          <span className={styles.resultadoValorDestacado}>
                            {formatLocal(convertCLP(oportunidad.mercadoCaptadoMensual))}/mes
                          </span>
                        </div>
                        <div className={styles.resultadoItem}>
                          <span className={styles.resultadoLabel}>Inversión referencia (3 meses):</span>
                          <span className={styles.resultadoValor}>
                            {formatLocal(convertCLP(INVERSION_REFERENCIA_3M_RETAIL.valor))}
                          </span>
                        </div>
                        <div className={styles.resultadoItem}>
                          <span className={styles.resultadoLabel}>Mercado captado anual:</span>
                          <span className={styles.resultadoValorDestacado}>
                            {formatLocal(convertCLP(oportunidad.mercadoCaptadoAnual))}
                          </span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {vistaActiva === "conveniencia" && resultadosConveniencia && (
                <div className={styles.simuladorContainer}>
                  <div className={styles.simuladorControles}>
                    <div className={styles.controlGroup}>
                      <label>Tienda objetivo:</label>
                      <select
                        value={convenienciaSeleccionada}
                        onChange={(e) => setConvenienciaSeleccionada(e.target.value)}
                        className="form-select form-select-sm"
                      >
                        {Object.keys(IPSOS_CONVENIENCIA.marketShare).map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className={styles.controlGroup}>
                      <label>% captación mercado (supuesto):</label>
                      <select
                        value={porcentajeCaptacion}
                        onChange={(e) => setPorcentajeCaptacion(Number(e.target.value))}
                        className="form-select form-select-sm"
                      >
                        {ESCENARIOS_CAPTACION_RETAIL.map((p) => (
                          <option key={p.valor} value={p.valor}>
                            {p.valor}%
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {(() => {
                    const oportunidad = calcularOportunidadConveniencia(convenienciaSeleccionada, porcentajeCaptacion);
                    if (!oportunidad) return null;

                    return (
                      <div className={styles.simuladorResultados}>
                        <div className={styles.resultadoItem}>
                          <span className={styles.resultadoLabel}>Mercado total mensual:</span>
                          <span className={styles.resultadoValor}>
                            {formatLocal(convertCLP(oportunidad.mercadoTotal))}
                          </span>
                        </div>
                        <div className={styles.resultadoItem}>
                          <span className={styles.resultadoLabel}>Captación al {porcentajeCaptacion}%:</span>
                          <span className={styles.resultadoValorDestacado}>
                            {formatLocal(convertCLP(oportunidad.mercadoCaptadoMensual))}/mes
                          </span>
                        </div>
                        <div className={styles.resultadoItem}>
                          <span className={styles.resultadoLabel}>Inversión referencia (3 meses):</span>
                          <span className={styles.resultadoValor}>
                            {formatLocal(convertCLP(INVERSION_REFERENCIA_3M_RETAIL.valor))}
                          </span>
                        </div>
                        <div className={styles.resultadoItem}>
                          <span className={styles.resultadoLabel}>Mercado captado anual:</span>
                          <span className={styles.resultadoValorDestacado}>
                            {formatLocal(convertCLP(oportunidad.mercadoCaptadoAnual))}
                          </span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
            <div className={styles.modalFooter}>
              <button type="button" className={styles.modalBtn} onClick={() => setShowOportunidadModal(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Retail;
